import { useState, useEffect, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "@/stores";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { AttributeValidationRow, AttributeValidationInsert } from "@/stores/admin/models";

interface ValidationDialogProps {
  attributeName: string;
  open: boolean;
  onClose: () => void;
}

type ValidationType = "enum" | "enum_multiselect" | "range" | "length" | "pattern";

const VALIDATION_TYPE_LABELS: Record<ValidationType, string> = {
  enum: "Allowed Values",
  enum_multiselect: "Allowed Values (multi-select)",
  range: "Value Range",
  length: "Max Length",
  pattern: "Pattern",
};

// enum and enum_multiselect are both EN/ET value lists and share the same editor.
const isEnumLike = (type: string): boolean => type === "enum" || type === "enum_multiselect";

// Track local edits keyed by rule id
interface EditState {
  validation_value: string;
  // Estonian translation for enum values ("" means no translation / null on save).
  validation_value_et: string;
  sort_order: number;
}

export const ValidationDialog = observer(
  ({ attributeName, open, onClose }: ValidationDialogProps) => {
    const { buildingDataAttributesStore } = useStores();
    const rules = buildingDataAttributesStore.getValidationsFor(attributeName);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState<number | null>(null);
    const [deleting, setDeleting] = useState<number | null>(null);

    const [edits, setEdits] = useState<Record<number, EditState>>({});

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [ruleToDelete, setRuleToDelete] = useState<AttributeValidationRow | null>(null);

    const [newType, setNewType] = useState<ValidationType>("enum");
    const [newValue, setNewValue] = useState("");
    const [newValueEt, setNewValueEt] = useState("");
    const [newSortOrder, setNewSortOrder] = useState(0);
    const [adding, setAdding] = useState(false);

    const loadRules = useCallback(async () => {
      setLoading(true);
      try {
        await buildingDataAttributesStore.loadValidationsForAttribute(attributeName);
      } catch (error) {
        console.error("Failed to load validation rules", error);
        toast.error("Failed to load validation rules");
      } finally {
        setLoading(false);
      }
    }, [attributeName, buildingDataAttributesStore]);

    useEffect(() => {
      if (open) {
        void loadRules();
      }
    }, [open, loadRules]);

    useEffect(() => {
      const editMap: Record<number, EditState> = {};
      for (const rule of rules) {
        editMap[rule.id] = {
          validation_value: rule.validation_value,
          validation_value_et: rule.validation_value_et ?? "",
          sort_order: rule.sort_order,
        };
      }
      setEdits(editMap);
    }, [rules]);

    // Defensive grouping: lazily create the bucket so an unforeseen
    // validation_type can never crash the modal (the previous fixed seed
    // omitted enum_multiselect, which broke every multi-select attribute).
    const grouped = rules.reduce<Record<string, AttributeValidationRow[]>>((acc, rule) => {
      (acc[rule.validation_type] ??= []).push(rule);
      return acc;
    }, {});

    const updateEdit = (id: number, field: keyof EditState, value: string | number) => {
      setEdits((prev) => ({
        ...prev,
        [id]: { ...prev[id], [field]: value },
      }));
    };

    const handleSave = async (rule: AttributeValidationRow) => {
      const edit = edits[rule.id];
      if (!edit) return;

      const nextValueEt = edit.validation_value_et.trim() || null;
      const currentValueEt = rule.validation_value_et || null;

      if (
        edit.validation_value === rule.validation_value &&
        nextValueEt === currentValueEt &&
        edit.sort_order === rule.sort_order
      ) {
        return;
      }

      setSaving(rule.id);
      try {
        await buildingDataAttributesStore.updateAttributeValidation(rule.id, {
          validation_value: edit.validation_value,
          validation_value_et: nextValueEt,
          sort_order: edit.sort_order,
        });
        toast.success("Validation rule updated");
      } catch (error) {
        console.error("Failed to update validation rule", error);
        toast.error("Failed to update validation rule");
      } finally {
        setSaving(null);
      }
    };

    const handleDeleteClick = (rule: AttributeValidationRow) => {
      setRuleToDelete(rule);
      setDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
      if (!ruleToDelete) return;

      setDeleting(ruleToDelete.id);
      try {
        await buildingDataAttributesStore.deleteAttributeValidation(
          ruleToDelete.id,
          ruleToDelete.attribute_nm
        );
        toast.success("Validation rule deleted");
      } catch (error) {
        console.error("Failed to delete validation rule", error);
        toast.error("Failed to delete validation rule");
      } finally {
        setDeleting(null);
        setDeleteConfirmOpen(false);
        setRuleToDelete(null);
      }
    };

    const handleAdd = async () => {
      if (!newValue.trim()) {
        toast.error("Validation value is required");
        return;
      }

      setAdding(true);
      try {
        const input: AttributeValidationInsert = {
          attribute_nm: attributeName,
          validation_type: newType,
          validation_value: newValue.trim(),
          validation_value_et: isEnumLike(newType) ? newValueEt.trim() || null : null,
          sort_order: newSortOrder,
        };
        await buildingDataAttributesStore.createAttributeValidation(input);
        toast.success("Validation rule added");
        setNewValue("");
        setNewValueEt("");
        setNewSortOrder(0);
      } catch (error) {
        console.error("Failed to add validation rule", error);
        toast.error("Failed to add validation rule");
      } finally {
        setAdding(false);
      }
    };

    const renderEnumSection = (enumRules: AttributeValidationRow[]) => (
      <div className="space-y-2">
        {enumRules.map((rule) => {
          const edit = edits[rule.id];
          if (!edit) return null;
          return (
            <div key={rule.id} className="flex items-center gap-2">
              <Input
                className="flex-1"
                value={edit.validation_value}
                onChange={(e) => updateEdit(rule.id, "validation_value", e.target.value)}
                onBlur={() => handleSave(rule)}
                placeholder="Value (EN)"
              />
              <Input
                className="flex-1"
                value={edit.validation_value_et}
                onChange={(e) => updateEdit(rule.id, "validation_value_et", e.target.value)}
                onBlur={() => handleSave(rule)}
                placeholder="Translation (ET)"
              />
              <Input
                className="w-20"
                type="number"
                value={edit.sort_order}
                onChange={(e) => updateEdit(rule.id, "sort_order", parseInt(e.target.value) || 0)}
                onBlur={() => handleSave(rule)}
                placeholder="Order"
              />
              {saving === rule.id ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteClick(rule)}
                  disabled={deleting === rule.id}
                >
                  {deleting === rule.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 text-destructive" />
                  )}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    );

    const renderRangeSection = (rangeRules: AttributeValidationRow[]) => (
      <div className="space-y-2">
        {rangeRules.map((rule) => {
          const edit = edits[rule.id];
          if (!edit) return null;
          const parts = edit.validation_value.split(",");
          const min = parts[0] ?? "";
          const max = parts[1] ?? "";
          return (
            <div key={rule.id} className="flex items-center gap-2">
              <div className="flex flex-1 items-center gap-2">
                <Label className="shrink-0 text-sm">Min</Label>
                <Input
                  type="number"
                  value={min}
                  onChange={(e) =>
                    updateEdit(rule.id, "validation_value", `${e.target.value},${max}`)
                  }
                  onBlur={() => handleSave(rule)}
                />
                <Label className="shrink-0 text-sm">Max</Label>
                <Input
                  type="number"
                  value={max}
                  onChange={(e) =>
                    updateEdit(rule.id, "validation_value", `${min},${e.target.value}`)
                  }
                  onBlur={() => handleSave(rule)}
                />
              </div>
              {saving === rule.id ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteClick(rule)}
                  disabled={deleting === rule.id}
                >
                  {deleting === rule.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 text-destructive" />
                  )}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    );

    const renderSingleValueSection = (
      singleRules: AttributeValidationRow[],
      inputType: string,
      placeholder: string
    ) => (
      <div className="space-y-2">
        {singleRules.map((rule) => {
          const edit = edits[rule.id];
          if (!edit) return null;
          return (
            <div key={rule.id} className="flex items-center gap-2">
              <Input
                className="flex-1"
                type={inputType}
                value={edit.validation_value}
                onChange={(e) => updateEdit(rule.id, "validation_value", e.target.value)}
                onBlur={() => handleSave(rule)}
                placeholder={placeholder}
              />
              {saving === rule.id ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteClick(rule)}
                  disabled={deleting === rule.id}
                >
                  {deleting === rule.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 text-destructive" />
                  )}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    );

    const hasRules = rules.length > 0;

    return (
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Validation Rules — {attributeName}</DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-6">
              {hasRules ? (
                Object.entries(grouped).map(([type, typeRules]) => {
                  if (typeRules.length === 0) return null;
                  return (
                    <div key={type}>
                      <h4 className="mb-2 text-sm font-medium text-muted-foreground">
                        {VALIDATION_TYPE_LABELS[type as ValidationType] ?? type}
                      </h4>
                      {isEnumLike(type) && renderEnumSection(typeRules)}
                      {type === "range" && renderRangeSection(typeRules)}
                      {type === "length" &&
                        renderSingleValueSection(typeRules, "number", "Max length")}
                      {type === "pattern" &&
                        renderSingleValueSection(typeRules, "text", "Regex pattern")}
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-muted-foreground">
                  No validation rules defined for this attribute.
                </p>
              )}

              <div className="border-t pt-4">
                <h4 className="mb-3 text-sm font-medium">Add Rule</h4>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="grid gap-1">
                    <Label className="text-xs">Type</Label>
                    <Select value={newType} onValueChange={(v) => setNewType(v as ValidationType)}>
                      <SelectTrigger className="w-[130px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="enum">Enum</SelectItem>
                        <SelectItem value="enum_multiselect">Enum (multi-select)</SelectItem>
                        <SelectItem value="range">Range</SelectItem>
                        <SelectItem value="length">Length</SelectItem>
                        <SelectItem value="pattern">Pattern</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid flex-1 gap-1">
                    <Label className="text-xs">
                      {isEnumLike(newType) ? "Value (EN)" : "Value"}
                    </Label>
                    <Input
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                      placeholder={
                        newType === "range"
                          ? "e.g., 0,10000"
                          : newType === "length"
                            ? "e.g., 255"
                            : newType === "pattern"
                              ? "e.g., ^[A-Z].*"
                              : "Allowed value"
                      }
                      disabled={adding}
                    />
                  </div>
                  {isEnumLike(newType) && (
                    <div className="grid flex-1 gap-1">
                      <Label className="text-xs">Translation (ET)</Label>
                      <Input
                        value={newValueEt}
                        onChange={(e) => setNewValueEt(e.target.value)}
                        placeholder="Optional"
                        disabled={adding}
                      />
                    </div>
                  )}
                  <div className="grid gap-1">
                    <Label className="text-xs">Order</Label>
                    <Input
                      className="w-20"
                      type="number"
                      value={newSortOrder}
                      onChange={(e) => setNewSortOrder(parseInt(e.target.value) || 0)}
                      disabled={adding}
                    />
                  </div>
                  <Button size="sm" onClick={handleAdd} disabled={adding || !newValue.trim()}>
                    {adding ? (
                      <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="mr-1 h-4 w-4" />
                    )}
                    Add
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>

        <ConfirmDialog
          open={deleteConfirmOpen}
          onOpenChange={setDeleteConfirmOpen}
          onConfirm={handleConfirmDelete}
          title="Delete Validation Rule"
          description={`Are you sure you want to delete this ${ruleToDelete?.validation_type ?? ""} validation rule "${ruleToDelete?.validation_value ?? ""}"? This action cannot be undone.`}
          confirmText="Delete"
          confirmVariant="destructive"
        />
      </Dialog>
    );
  }
);
